// =============================================================================
// V2ChatInner.tsx — Inner chat UI consuming NotesToHankContext
//
// Renders the message list, typing indicator, attachment preview, and input bar.
// Implements smart auto-scroll (150px threshold) and connection state banners.
// =============================================================================

import React, { useRef, useCallback, useEffect, useMemo } from 'react';
import { AlertCircle, RefreshCw, ArrowLeft } from 'lucide-react';
import { Button } from '../ui/button';
import { useNotesToHank } from '../../../application/context/NotesToHankContext/NotesToHankContext';
import { isSendEnabled } from '../../../application/services/notesToHank/notesToHankMessageUtils';
import {
  uploadPhotoBatch,
  buildCloudFrontUrl,
} from '../../../application/services/notesToHank/photoUploadService';
import type { AttachmentItem } from '../../../application/services/notesToHank/NotesToHankTypes';
import { V2MessageBubble } from './V2MessageBubble';
import { V2TypingIndicator } from './V2TypingIndicator';
import { V2AttachmentPreview } from './V2AttachmentPreview';
import { V2ChatInput } from './V2ChatInput';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface V2ChatInnerProps {
  dealId: string;
  dealInfo?: any;
  onBack: () => void;
  fileInputRef: React.RefObject<HTMLInputElement>;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function V2ChatInner({
  dealId,
  dealInfo: _dealInfo,
  onBack,
  fileInputRef,
}: V2ChatInnerProps) {
  const {
    messages,
    isWaitingForResponse,
    connectionState,
    inputText,
    setInputText,
    pendingPhotos,
    pendingAttachments,
    selectedAnswerIds,
    pressedActionButtonIds,
    sendMessage,
    selectAnswer,
    submitBatchAnswers,
    pressActionButton,
    addAttachment,
    addAttachments,
    removeAttachment,
    perQuestionAttachments,
    removeQuestionAttachment,
    activeUploadTaskId,
    setTaskUploading,
    setTaskUploadComplete,
    replaceUploadingAttachments,
    retryFailedUpload,
    storeFilesForRetry,
    retryLastMessage,
    isUploading,
  } = useNotesToHank();

  // -------------------------------------------------------------------------
  // Smart Auto-Scroll
  // -------------------------------------------------------------------------

  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const prevMessageCountRef = useRef(0);
  const initialScrollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const hasUserScrolledRef = useRef(false);

  const scrollToBottom = useCallback((behavior: ScrollBehavior = 'smooth') => {
    messagesEndRef.current?.scrollIntoView({ behavior });
  }, []);

  const isNearBottom = useCallback(() => {
    const el = messagesContainerRef.current;
    if (!el) return true;
    const threshold = 150;
    return el.scrollHeight - el.scrollTop - el.clientHeight <= threshold;
  }, []);

  // Track if user has manually scrolled up
  useEffect(() => {
    const el = messagesContainerRef.current;
    if (!el) return;
    const handleScroll = () => {
      hasUserScrolledRef.current = !isNearBottom();
    };
    el.addEventListener('scroll', handleScroll, { passive: true });
    return () => el.removeEventListener('scroll', handleScroll);
  }, [isNearBottom]);

  // Scroll to bottom whenever messages change — debounced for initial batch load
  useEffect(() => {
    const prevCount = prevMessageCountRef.current;
    prevMessageCountRef.current = messages.length;

    if (messages.length === 0) return;

    // Clear any pending scroll timer (messages are still arriving)
    if (initialScrollTimerRef.current) {
      clearTimeout(initialScrollTimerRef.current);
    }

    // If user just sent a message, always scroll immediately
    if (messages.length > prevCount && prevCount > 0) {
      const lastMessage = messages[messages.length - 1];
      if (lastMessage?.sender === 'user') {
        scrollToBottom('smooth');
        return;
      }
    }

    // For initial load or incoming messages: debounce scroll to let batch complete
    // Only auto-scroll if user hasn't manually scrolled up
    if (!hasUserScrolledRef.current) {
      initialScrollTimerRef.current = setTimeout(() => {
        scrollToBottom(prevCount === 0 ? 'instant' : 'smooth');
      }, 150);
    }

    return () => {
      if (initialScrollTimerRef.current) {
        clearTimeout(initialScrollTimerRef.current);
      }
    };
  }, [messages, scrollToBottom]);

  // -------------------------------------------------------------------------
  // Derived State
  // -------------------------------------------------------------------------

  const allAttachments = useMemo(
    () => [...pendingPhotos, ...pendingAttachments],
    [pendingPhotos, pendingAttachments]
  );

  const canSend = isSendEnabled(
    inputText,
    selectedAnswerIds,
    pressedActionButtonIds
  );
  const sendDisabled = !canSend || isWaitingForResponse;

  const pressedActionSet = useMemo(
    () => new Set(pressedActionButtonIds),
    [pressedActionButtonIds]
  );

  // -------------------------------------------------------------------------
  // File Input Handler
  // -------------------------------------------------------------------------

  const handleFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (!files || files.length === 0) return;

      const taskId = activeUploadTaskId;
      const orgId = (global as any)?.me?.orgID;
      const userId = (global as any)?.me?.id;

      if (taskId && orgId && userId) {
        // S3 upload path: create placeholders, upload, then replace
        const placeholders: AttachmentItem[] = Array.from(files).map(
          (file) => ({
            uri: URL.createObjectURL(file),
            type: 'photo' as const,
            mimeType: file.type,
            fileName: file.name,
            timestamp: Date.now(),
            uploadStatus: 'uploading' as const,
          })
        );

        storeFilesForRetry(taskId, Array.from(files));
        addAttachments(placeholders);
        setTaskUploading(taskId);

        const { succeeded, failed } = await uploadPhotoBatch(
          Array.from(files),
          dealId,
          taskId,
          orgId,
          userId
        );

        const completedAttachments: AttachmentItem[] = succeeded.map((r) => ({
          uri: buildCloudFrontUrl(r.s3Key),
          type: 'photo' as const,
          s3Key: r.s3Key,
          timestamp: Date.now(),
          uploadStatus: 'complete' as const,
        }));

        const erroredAttachments: AttachmentItem[] = failed.map((f) => ({
          uri: '',
          type: 'photo' as const,
          fileName: f.fileName,
          timestamp: Date.now(),
          uploadStatus: 'error' as const,
          errorMessage: f.error,
        }));

        replaceUploadingAttachments(
          taskId,
          completedAttachments,
          erroredAttachments
        );
        setTaskUploadComplete(taskId);

        // Revoke blob URLs to free memory
        placeholders.forEach((p) => {
          if (p.uri.startsWith('blob:')) {
            URL.revokeObjectURL(p.uri);
          }
        });
      } else {
        // Fallback: blob-URL-only mode
        const blobAttachments: AttachmentItem[] = Array.from(files).map(
          (file) => {
            const url = URL.createObjectURL(file);
            const isImage = file.type.startsWith('image/');
            return {
              uri: url,
              type: isImage ? ('photo' as const) : ('file' as const),
              mimeType: file.type,
              fileName: file.name,
              timestamp: Date.now(),
            };
          }
        );
        blobAttachments.forEach((att) => addAttachment(att));
      }

      // Reset input so the same file can be selected again
      e.target.value = '';
    },
    [
      activeUploadTaskId,
      dealId,
      addAttachment,
      addAttachments,
      setTaskUploading,
      setTaskUploadComplete,
      replaceUploadingAttachments,
      storeFilesForRetry,
    ]
  );

  // -------------------------------------------------------------------------
  // Attach button handler
  // -------------------------------------------------------------------------

  const handleAttachPress = useCallback(() => {
    fileInputRef.current?.click();
  }, [fileInputRef]);

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <div className="flex flex-col h-full bg-background">
      {/* Header with Back button */}
      <div className="flex items-center px-3 py-2 border-b border-border">
        <Button
          variant="ghost"
          size="icon"
          onClick={onBack}
          aria-label="Back to inspection list"
          className="mr-2"
        >
          <ArrowLeft className="size-4" />
        </Button>
        <span className="text-sm font-semibold text-foreground">
          AI Inspection
        </span>
      </div>

      {/* Connection error banner */}
      {connectionState === 'error' && (
        <div className="flex items-center justify-between px-3 py-2 bg-destructive/10 border-b border-destructive/20">
          <div className="flex items-center gap-2 text-destructive text-sm">
            <AlertCircle className="size-4" />
            <span>Connection failed</span>
          </div>
          <Button
            variant="destructive"
            size="sm"
            onClick={retryLastMessage}
            aria-label="Retry connection"
          >
            <RefreshCw className="size-3 mr-1" />
            Retry
          </Button>
        </div>
      )}

      {/* Reconnecting / connecting banner */}
      {(connectionState === 'reconnecting' ||
        connectionState === 'connecting') && (
        <div className="flex items-center justify-center px-3 py-1.5 bg-primary/10 border-b border-primary/20">
          <RefreshCw className="size-3 mr-1.5 animate-spin text-primary" />
          <span className="text-xs font-medium text-primary">
            {connectionState === 'reconnecting'
              ? 'Reconnecting...'
              : 'Connecting...'}
          </span>
        </div>
      )}

      {/* Messages area */}
      <div
        ref={messagesContainerRef}
        className="flex-1 overflow-y-auto py-2 px-3"
      >
        {messages.length === 0 ? (
          <div className="flex items-center justify-center px-4 py-6">
            <p className="text-sm text-muted-foreground text-center">
              Start an AI-assisted inspection for this deal.
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            {messages.map((item) => {
              const isUser = item.sender === 'user';
              // Merge persisted perQuestionAttachments from the message with
              // live context state. Live context takes precedence during an
              // active session; persisted data restores thumbnails after reload.
              const mergedAttachments = {
                ...(item.perQuestionAttachments || {}),
                ...(perQuestionAttachments || {}),
              };
              return (
                <div key={item.id}>
                  <V2MessageBubble
                    message={item}
                    isUser={isUser}
                    onAnswerSelect={selectAnswer}
                    onBatchSubmit={submitBatchAnswers}
                    onActionPress={pressActionButton}
                    pressedActionButtonIds={pressedActionSet}
                    questionAttachments={mergedAttachments}
                    onRemoveQuestionAttachment={removeQuestionAttachment}
                    onRetryUpload={retryFailedUpload}
                    isUploading={isUploading}
                  />
                  {item.isError && (
                    <div className="px-1.5 mb-1">
                      <Button
                        variant="default"
                        size="sm"
                        onClick={retryLastMessage}
                        aria-label="Retry sending message"
                        className="text-xs"
                      >
                        Retry
                      </Button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Typing indicator */}
        {isWaitingForResponse && (
          <div className="mt-2">
            <V2TypingIndicator />
          </div>
        )}

        {/* Scroll anchor */}
        <div ref={messagesEndRef} />
      </div>

      {/* Attachment preview strip */}
      {allAttachments.length > 0 && (
        <V2AttachmentPreview
          attachments={allAttachments}
          onRemove={removeAttachment}
        />
      )}

      {/* Chat input */}
      <V2ChatInput
        value={inputText}
        onChangeText={setInputText}
        onSend={sendMessage}
        sendDisabled={sendDisabled}
        onAttachPress={handleAttachPress}
        pendingPhotoCount={allAttachments.length}
      />

      {/* Hidden file input for web attachment picking */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
        onChange={handleFileChange}
        className="hidden"
      />
    </div>
  );
}

export default V2ChatInner;
